import React, { useState } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import { Edit3, Trash2, Check, X } from 'lucide-react';
import { TodoItem as TodoItemType } from '../types/todos';

interface TodoItemProps {
    todo: TodoItemType;
    userNames: { [key: string]: string | null };
    onToggle: () => void;
    onEdit: (newText: string) => Promise<void>;
    onDelete: () => void;
    submitting?: boolean;
}

export const TodoItem: React.FC<TodoItemProps> = ({
    todo,
    userNames,
    onToggle,
    onEdit,
    onDelete,
    submitting = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(todo.text);
    const [localSubmitting, setLocalSubmitting] = useState(false);

    const handleSaveEdit = async () => {
        if (!editText.trim()) return;

        setLocalSubmitting(true);
        try {
            await onEdit(editText);
            setIsEditing(false);
        } catch (err) {
            console.error('Error saving edit:', err);
        } finally {
            setLocalSubmitting(false);
        }
    };

    const handleCancelEdit = () => {
        setEditText(todo.text);
        setIsEditing(false);
    };

    return (
        <Card
            className={`todo-card dropshadow dropshadow-hover ${todo.done ? 'todo-completed' : ''}`}
        >
            <Card.Body className="todo-card-body">
                {isEditing ? (
                    <div className="todo-edit-mode">
                        <Form.Control
                            type="text"
                            value={editText}
                            onChange={(e) => {
                                // Replace newlines with spaces
                                const textWithSpaces = e.target.value.replace(/\n/g, ' ');
                                setEditText(textWithSpaces);
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveEdit();
                                }
                                if (e.key === 'Escape') {
                                    handleCancelEdit();
                                }
                            }}
                            autoFocus
                            disabled={localSubmitting || submitting}
                        />
                        <div className="todo-edit-actions">
                            <Button
                                size="sm"
                                variant="success"
                                onClick={handleSaveEdit}
                                disabled={localSubmitting || submitting}
                            >
                                <Check size={16} />
                            </Button>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={handleCancelEdit}
                                disabled={localSubmitting || submitting}
                            >
                                <X size={16} />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="todo-card-content">
                        {/* A label, so a tap anywhere around the box toggles it */}
                        <label className="todo-checkbox-wrapper">
                            <Form.Check.Input
                                type="checkbox"
                                checked={todo.done}
                                onChange={onToggle}
                                // onChange toggles; toggling here as well wrote every change twice
                                onClick={(e) => e.stopPropagation()}
                                className="todo-checkbox"
                                aria-label={todo.done ? 'Markera som ej klar' : 'Markera som klar'}
                            />
                        </label>
                        <div className="todo-content">
                            <div className="todo-text">{todo.text}</div>
                            <div className="todo-metadata">
                                <span className="todo-meta-item">
                                    Skapad av {userNames[todo.createdBy] || 'Okänd användare'} {todo.createdAt}
                                </span>
                                {todo.done && todo.completedBy && todo.completedAt && (
                                    <>
                                        <span className="todo-meta-separator">•</span>
                                        <span className="todo-meta-item">
                                            Klarmarkerad av {userNames[todo.completedBy] || 'Okänd användare'} {todo.completedAt}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="todo-actions">
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setIsEditing(true)}
                            >
                                <Edit3 size={16} />
                            </Button>
                            <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={onDelete}
                            >
                                <Trash2 size={16} />
                            </Button>
                        </div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};
